import { useEffect, useMemo, useState } from 'react';
import {
  cacheVisitTypeLegacyFlags,
  isCreatableVisitTypeId,
  isOrderType,
  type TaskTypeId,
} from '../constants/visitTypes';
import {
  getOrderTypeOptionsOrdersTypeOptionsGet,
  getVisitTypeOptionsVisitsTypeOptionsGet,
} from '../services/api/sdk.gen';
import type {
  OrderTypeOption,
  VisitTypeId,
  VisitTypeOption,
  VisitTypeOptionAlias,
} from '../services/api/types.gen';

/**
 * Unified shape for the create-task picker. Merges visit type options
 * (`/visits/type-options`) and order type options (`/orders/type-options`)
 * into a single list, with `uses_roger_doc_form` synthesized to `false`
 * for order types (orders always use EMR-side forms).
 */
export type TaskTypeOption = {
  id: TaskTypeId;
  display_name: string;
  uses_roger_doc_form: boolean;
  /** From ``/visits/type-options``; true for legacy OASIS-E2 types. */
  is_legacy: boolean;
  /** Other names this visit type can be scheduled under. */
  aliases?: VisitTypeOptionAlias[];
  /**
   * Visit types only: a new visit of this type starts non-billable for the
   * agency, so it would not count toward LUPA. Unset for order types.
   */
  non_billable?: boolean;
  /**
   * Set on an alias option made by ``withAliasOptions``: ``id`` is then only a
   * picker key, and this is the visit type the task is created as.
   */
  visit_type_id?: TaskTypeId;
  alias_id?: string;
};

/** The task type an option creates; differs from ``id`` for alias options. */
export function optionTypeId(option: {
  id: string;
  visit_type_id?: TaskTypeId;
}): TaskTypeId {
  return option.visit_type_id ?? (option.id as TaskTypeId);
}

/**
 * Add one option per alias, right after its visit type, for create pickers.
 * Each alias option has a unique ``id`` key and carries its visit type and
 * alias id, so a selection creates a visit of that type under the alias name.
 */
export function withAliasOptions(options: TaskTypeOption[]): TaskTypeOption[] {
  return options.flatMap(option => [
    option,
    ...(option.aliases ?? []).map(alias => ({
      ...option,
      id: `alias:${alias.id}` as TaskTypeId,
      display_name: alias.name,
      aliases: [],
      visit_type_id: option.id,
      alias_id: alias.id,
    })),
  ]);
}

let cachedOptions: TaskTypeOption[] | null = null;
let cachedDisplayNames: Record<string, string> | null = null;
let fetchPromise: Promise<TaskTypeOption[]> | null = null;

/**
 * Drop the cached task type options so the next mount refetches.
 *
 * ``/visits/type-options`` is no longer a constant catalog -- it omits types
 * the agency's WorkOS feature flags don't grant (EMR-1360). Anything that
 * refreshes those flags mid-session must clear this cache, or the picker keeps
 * serving the pre-refresh catalog until a full page load.
 */
export function clearVisitTypeOptionsCache(): void {
  cachedOptions = null;
  cachedDisplayNames = null;
  fetchPromise = null;
}

function mergeOptions(
  visits: VisitTypeOption[],
  orders: OrderTypeOption[]
): TaskTypeOption[] {
  const merged: TaskTypeOption[] = visits.map(v => ({
    id: v.id,
    display_name: v.display_name,
    uses_roger_doc_form: v.uses_roger_doc_form,
    is_legacy: v.is_legacy ?? false,
    non_billable: v.non_billable,
    aliases: v.aliases ?? [],
  }));
  const seen = new Set<string>(merged.map(o => o.id));
  for (const o of orders) {
    if (seen.has(o.id)) continue;
    merged.push({
      id: o.id,
      display_name: o.display_name,
      uses_roger_doc_form: false,
      is_legacy: false,
    });
  }
  return merged;
}

async function fetchOptions(): Promise<TaskTypeOption[]> {
  if (cachedOptions) return cachedOptions;
  if (fetchPromise) return fetchPromise;

  fetchPromise = Promise.all([
    getVisitTypeOptionsVisitsTypeOptionsGet({}),
    getOrderTypeOptionsOrdersTypeOptionsGet({}),
  ])
    .then(([visitsRes, ordersRes]) => {
      const visitsRaw: unknown = visitsRes.data;
      const ordersRaw: unknown = ordersRes.data;
      if (!Array.isArray(visitsRaw) || !Array.isArray(ordersRaw)) {
        throw new Error('Unexpected task type options payload shape');
      }
      const data = mergeOptions(
        visitsRaw as VisitTypeOption[],
        ordersRaw as OrderTypeOption[]
      );
      cachedOptions = data;
      cachedDisplayNames = Object.fromEntries(
        data.map(o => [o.id, o.display_name])
      );
      cacheVisitTypeLegacyFlags(data);
      return data;
    })
    .catch(() => {
      fetchPromise = null;
      return [];
    });

  return fetchPromise;
}

/**
 * Hook that fetches the merged visit + order type options from the backend
 * and caches them. Returns the merged options list and a loading flag.
 *
 * Public name retained for backwards-compat with existing call sites; the
 * returned list now spans both visits and orders (see :type:`TaskTypeOption`).
 */
export function useVisitTypeOptions() {
  const [fetchedOptions, setFetchedOptions] = useState<TaskTypeOption[]>(
    cachedOptions ?? []
  );
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    if (cachedOptions) {
      setFetchedOptions(cachedOptions);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchOptions().then(data => {
      if (!cancelled) {
        setFetchedOptions(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () =>
      fetchedOptions.filter(o =>
        isOrderType(o.id) ? isCreatableVisitTypeId(o.id) : !o.is_legacy
      ),
    [fetchedOptions]
  );

  return { options, loading };
}

/**
 * Whether this visit type has a RogerDoc form schema (from ``/visits/type-options``).
 * Returns false if ``options`` is not loaded yet or the id is missing (safe default).
 */
export function visitTypeUsesRogerDocForm(
  visitType: VisitTypeId,
  options: ReadonlyArray<TaskTypeOption>
): boolean {
  return options.find(o => o.id === visitType)?.uses_roger_doc_form ?? false;
}

/**
 * Get a human-readable display name for a visit or order type ID.
 * Uses the cached backend data when available, falls back to basic formatting.
 */
export function getVisitTypeDisplayName(visitType: string): string {
  if (cachedDisplayNames && visitType in cachedDisplayNames) {
    return cachedDisplayNames[visitType];
  }
  return visitType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}
